import { useState, useEffect, useCallback, useMemo } from "react";
import { Filter, Search, CalendarIcon, Settings } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { MultiSelect } from "@/components/ui/multiselect";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { formatDate, cn, toLocalDateString, parseLocalDate } from "@/lib/utils";
import { TransactionTable } from "@/components/transactions/transaction-table";
import { ColumnConfigDialog, type ColumnDefinition } from "@/components/shared/column-config-dialog";
import { useScreenColumnPreferences } from "@/hooks/useScreenColumnPreferences";
import { UniqueTableNamesForColumnConfiguration } from "@/config/unique-table-names-for-column-configuration";
import { useAddPromiseTransactionsPaginatedGraphQL, type GetAddPromiseTransactionsParams } from "@/hooks/useAddPromiseTransactionsGraphQL";
import { useAddCaseTransactionsPaginatedGraphQL, type GetAddCaseTransactionsParams } from "@/hooks/useAddCaseTransactionsGraphQL";
import { useCurrenciesList } from "@/hooks/useCurrenciesData";
import type { Transaction, TransactionType, TransactionStatus, ColumnVisibility } from "@/types/transaction";

// Default columns for the Select Transactions modal — same as Promise Details except promisedAmount
const DEFAULT_SELECT_TX_COLUMNS: ColumnDefinition[] = [
  { id: 'referenceNumber', label: 'Reference Number', visible: true, order: 0, locked: true },
  { id: 'transactionDate', label: 'Transaction Date', visible: true, order: 1 },
  { id: 'dueDate', label: 'Due Date', visible: true, order: 2 },
  { id: 'transactionType', label: 'Type', visible: true, order: 3 },
  { id: 'amount', label: 'Gross', visible: true, order: 4 },
  { id: 'currency', label: 'Currency', visible: false, order: 5 },
  { id: 'outstandingAmount', label: 'Outstanding', visible: true, order: 6 },
  { id: 'status', label: 'Status', visible: true, order: 7 },
  { id: 'description', label: 'Description', visible: false, order: 8 },
  { id: 'accountCode', label: 'Account', visible: false, order: 9 },
  { id: 'netAmount', label: 'Net', visible: false, order: 10 },
  { id: 'vatAmount', label: 'Tax', visible: false, order: 11 },
  { id: 'subLedger', label: 'Sub Ledger', visible: false, order: 12 },
  { id: 'orderNumber', label: 'Order Number', visible: false, order: 13 },
  { id: 'createdBy', label: 'Created By', visible: false, order: 14 },
  { id: 'modifiedBy', label: 'Modified By', visible: false, order: 15 },
  { id: 'customerName', label: 'Customer Name', visible: false, order: 16 },
  { id: 'flags', label: 'Flags', visible: true, order: 17 },
  { id: 'onDispute', label: 'External Dispute', visible: false, order: 18 },
];

const transactionTypes: { value: TransactionType; label: string }[] = [
  { value: "invoice", label: "Invoice" },
  { value: "credit_note", label: "Credit Note" },
  { value: "debit_adjustment", label: "Debit Adjustment" },
  { value: "credit_adjustment", label: "Credit Adjustment" },
];

const statuses: { value: TransactionStatus; label: string }[] = [
  { value: "outstanding", label: "Outstanding" },
  { value: "overdue", label: "Overdue" },
];

interface SelectTransactionsModalFilters {
  transactionTypes: TransactionType[];
  statuses: TransactionStatus[];
  currencies: string[];
  dateFrom?: string;
  dateTo?: string;
  amountMin?: number;
  amountMax?: number;
  outstandingBalanceMin?: number;
  outstandingBalanceMax?: number;
  onDispute?: boolean;
  hasCase?: boolean;
}

const emptyFilters: SelectTransactionsModalFilters = {
  transactionTypes: [],
  statuses: [],
  currencies: [],
};

type SelectTransactionsModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customerId: number;
  onAddTransactions: (transactions: Transaction[]) => void;
  currency?: string;
} & (
  | { context: "promise"; promiseId: number }
  | { context: "case"; caseId: number }
);

export function SelectTransactionsModal(props: SelectTransactionsModalProps) {
  const { open, onOpenChange, customerId, onAddTransactions, context, currency } = props;
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [sortBy, setSortBy] = useState("transactionDate");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState<SelectTransactionsModalFilters>({ ...emptyFilters });

  // Column configuration
  const {
    columns: modalColumns,
    savePreferences: saveModalColumnPrefs,
    resetToDefaults: resetModalColumnPrefs,
  } = useScreenColumnPreferences(UniqueTableNamesForColumnConfiguration.SelectTransactionsModal, DEFAULT_SELECT_TX_COLUMNS);

  const columnVisibility = useMemo(() => {
    const vis = {} as ColumnVisibility;
    for (const col of modalColumns) {
      vis[col.id as keyof ColumnVisibility] = col.visible;
    }
    return vis;
  }, [modalColumns]);

  const handleColumnConfigApply = useCallback((columns: ColumnDefinition[]) => {
    saveModalColumnPrefs(columns);
  }, [saveModalColumnPrefs]);

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setSearchQuery("");
      setCurrentPage(1);
      setSelectedRows(new Set());
      setAppliedFilters({ ...emptyFilters });
      setFilterSheetOpen(false);
    }
  }, [open]);

  const effectiveCurrencies = currency
    ? [currency]
    : appliedFilters.currencies.length > 0 ? appliedFilters.currencies : undefined;

  const sharedQueryParams = {
    page: currentPage,
    pageSize,
    search: searchQuery || undefined,
    sortBy,
    sortOrder,
    transactionTypes: appliedFilters.transactionTypes.length > 0 ? appliedFilters.transactionTypes : undefined,
    statuses: appliedFilters.statuses.length > 0 ? appliedFilters.statuses : undefined,
    currencies: effectiveCurrencies,
    dateFrom: appliedFilters.dateFrom,
    dateTo: appliedFilters.dateTo,
    amountMin: appliedFilters.amountMin,
    amountMax: appliedFilters.amountMax,
    outstandingBalanceMin: appliedFilters.outstandingBalanceMin,
    outstandingBalanceMax: appliedFilters.outstandingBalanceMax,
    onDispute: appliedFilters.onDispute,
  };

  const promiseQueryParams: GetAddPromiseTransactionsParams = {
    ...sharedQueryParams,
    hasCase: appliedFilters.hasCase,
  };

  const caseQueryParams: GetAddCaseTransactionsParams = {
    ...sharedQueryParams,
    hasPromise: appliedFilters.hasCase,
  };

  const promiseResult = useAddPromiseTransactionsPaginatedGraphQL(
    open && context === "promise" ? customerId : undefined,
    open && context === "promise" ? (props as { promiseId: number }).promiseId : undefined,
    promiseQueryParams,
  );

  const caseResult = useAddCaseTransactionsPaginatedGraphQL(
    open && context === "case" ? customerId : undefined,
    open && context === "case" ? (props as { caseId: number }).caseId : undefined,
    caseQueryParams,
  );

  const { transactions, pagination } = context === "promise" ? promiseResult : caseResult;

  const handleSortChange = useCallback((newSortBy: string, newSortOrder: "asc" | "desc") => {
    setSortBy(newSortBy);
    setSortOrder(newSortOrder);
  }, []);

  const handleSelectRow = useCallback((id: number, checked: boolean) => {
    setSelectedRows((prev) => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(id);
      } else {
        newSet.delete(id);
      }
      return newSet;
    });
  }, []);

  const handleSelectAll = useCallback(
    (checked: boolean) => {
      if (checked) {
        setSelectedRows(new Set(transactions.map((tx) => tx.id)));
      } else {
        setSelectedRows(new Set());
      }
    },
    [transactions],
  );

  const handleAdd = useCallback(() => {
    const selected = transactions.filter((tx) => selectedRows.has(tx.id));
    onAddTransactions(selected);
    onOpenChange(false);
  }, [transactions, selectedRows, onAddTransactions, onOpenChange]);

  const activeFilterCount =
    (appliedFilters.transactionTypes.length > 0 ? 1 : 0) +
    (appliedFilters.statuses.length > 0 ? 1 : 0) +
    (appliedFilters.currencies.length > 0 ? 1 : 0) +
    (appliedFilters.dateFrom || appliedFilters.dateTo ? 1 : 0) +
    (appliedFilters.amountMin !== undefined || appliedFilters.amountMax !== undefined ? 1 : 0) +
    (appliedFilters.outstandingBalanceMin !== undefined || appliedFilters.outstandingBalanceMax !== undefined ? 1 : 0) +
    (appliedFilters.onDispute !== undefined ? 1 : 0) +
    (appliedFilters.hasCase !== undefined ? 1 : 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[75vw] h-[85vh] flex flex-col">
        <DialogHeader className="shrink-0">
          <DialogTitle>Select Transactions</DialogTitle>
          <DialogDescription>
            {context === "promise"
              ? "Select transactions to add to this promise. Transactions already linked to this promise are excluded."
              : "Select transactions to add to this case. Transactions already linked to this case are excluded."}
          </DialogDescription>
        </DialogHeader>

        {/* Toolbar */}
        <div className="shrink-0 flex items-center justify-between gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setFilterSheetOpen(true)}
            className="gap-2"
          >
            <Filter className="h-4 w-4" />
            Filter
            {activeFilterCount > 0 && (
              <Badge variant="secondary" className="ml-1 h-5 w-5 rounded-full p-0 flex items-center justify-center text-xs">
                {activeFilterCount}
              </Badge>
            )}
          </Button>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                type="search"
                placeholder="Quick search..."
                className="pl-8 h-9 w-[250px]"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div>
                    <ColumnConfigDialog
                      columns={modalColumns}
                      onApply={handleColumnConfigApply}
                      onReset={resetModalColumnPrefs}
                      trigger={
                        <Button variant="outline" size="icon" className="h-9 w-9" title="Configure Columns">
                          <Settings className="h-4 w-4" />
                        </Button>
                      }
                    />
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Configure columns</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>

        {/* Table — vertical scroll only */}
        <div className="flex-1 min-h-0 overflow-y-auto">
          <TransactionTable
            transactions={transactions}
            columnVisibility={columnVisibility}
            orderedColumns={modalColumns}
            onViewTransaction={() => {}}
            showCustomerColumn={false}
            displayAmounts={true}
            enableSelection
            selectedRows={selectedRows}
            onSelectRow={handleSelectRow}
            onSelectAll={handleSelectAll}
            currentPage={currentPage}
            pageSize={pageSize}
            totalItems={pagination.totalItems}
            onPageChange={setCurrentPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setCurrentPage(1);
            }}
            onSortChange={handleSortChange}
          />
        </div>

        {/* Footer */}
        <DialogFooter className="shrink-0 flex items-center justify-between sm:justify-between">
          <span className="text-sm text-muted-foreground">
            {selectedRows.size > 0
              ? `${selectedRows.size} transaction${selectedRows.size === 1 ? "" : "s"} selected`
              : "No transactions selected"}
          </span>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={handleAdd} disabled={selectedRows.size === 0}>
              Add {selectedRows.size > 0 ? `(${selectedRows.size})` : ""}
            </Button>
          </div>
        </DialogFooter>

        {/* Filter Sheet (inside modal) */}
        <SelectTransactionsFilterSheet
          open={filterSheetOpen}
          onOpenChange={setFilterSheetOpen}
          filters={appliedFilters}
          hideCurrencyFilter={!!currency}
          onApplyFilters={(filters) => {
            setAppliedFilters(filters);
            setCurrentPage(1);
            setFilterSheetOpen(false);
          }}
          onClearAll={() => {
            setAppliedFilters({ ...emptyFilters });
            setCurrentPage(1);
            setFilterSheetOpen(false);
          }}
        />
      </DialogContent>
    </Dialog>
  );
}

// --- Filter Sheet (simplified for promise context) ---

interface SelectTransactionsFilterSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  filters: SelectTransactionsModalFilters;
  hideCurrencyFilter?: boolean;
  onApplyFilters: (filters: SelectTransactionsModalFilters) => void;
  onClearAll: () => void;
}

function SelectTransactionsFilterSheet({
  open,
  onOpenChange,
  filters,
  hideCurrencyFilter,
  onApplyFilters,
  onClearAll,
}: SelectTransactionsFilterSheetProps) {
  const [draftFilters, setDraftFilters] = useState<SelectTransactionsModalFilters>({ ...filters });
  const [dateFromOpen, setDateFromOpen] = useState(false);
  const [dateToOpen, setDateToOpen] = useState(false);

  useEffect(() => {
    if (open) {
      setDraftFilters({ ...filters });
    }
  }, [open, filters]);

  const { data: currencies } = useCurrenciesList();

  const currencyOptions = (currencies ?? []).map((c) => ({
    value: c.code,
    label: `${c.code} - ${c.name}`,
  }));

  const handleApply = () => {
    onApplyFilters(draftFilters);
  };

  const handleClear = () => {
    setDraftFilters({ ...emptyFilters });
    onClearAll();
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-lg flex flex-col overflow-hidden">
        <SheetHeader className="px-6">
          <SheetTitle>Filter Transactions</SheetTitle>
          <SheetDescription>
            Refine the transaction list
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto pr-4 pl-4 mt-6">
          <div className="space-y-6 pb-4">
            {/* Transaction Type Filter */}
            <div className="space-y-2">
              <Label>Transaction Type</Label>
              <MultiSelect
                options={transactionTypes}
                placeholder="Select transaction types..."
                emptyMessage="No transaction types found."
                value={draftFilters.transactionTypes}
                onValueChange={(value) =>
                  setDraftFilters({ ...draftFilters, transactionTypes: value as TransactionType[] })
                }
              />
            </div>

            {/* Status Filter */}
            <div className="space-y-2">
              <Label>Status</Label>
              <MultiSelect
                options={statuses}
                placeholder="Select statuses..."
                emptyMessage="No statuses found."
                value={draftFilters.statuses}
                onValueChange={(value) =>
                  setDraftFilters({ ...draftFilters, statuses: value as TransactionStatus[] })
                }
              />
            </div>

            {/* Currency Filter */}
            {!hideCurrencyFilter && (
              <div className="space-y-2">
                <Label>Currency</Label>
                <MultiSelect
                  options={currencyOptions}
                  placeholder="Select currencies..."
                  emptyMessage="No currencies found."
                  value={draftFilters.currencies}
                  onValueChange={(value) =>
                    setDraftFilters({ ...draftFilters, currencies: value })
                  }
                />
              </div>
            )}

            <Separator />

            {/* Transaction Date Filter */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">Transaction Date</Label>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="modal-date-from" className="text-sm">From</Label>
                  <Popover open={dateFromOpen} onOpenChange={setDateFromOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        id="modal-date-from"
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !draftFilters.dateFrom && "text-muted-foreground",
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {draftFilters.dateFrom ? formatDate(draftFilters.dateFrom) : "Pick a date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={draftFilters.dateFrom ? parseLocalDate(draftFilters.dateFrom) : undefined}
                        onSelect={(date) => {
                          setDraftFilters({
                            ...draftFilters,
                            dateFrom: date ? toLocalDateString(date) : undefined,
                          });
                          setDateFromOpen(false);
                        }}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="modal-date-to" className="text-sm">To</Label>
                  <Popover open={dateToOpen} onOpenChange={setDateToOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        id="modal-date-to"
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !draftFilters.dateTo && "text-muted-foreground",
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {draftFilters.dateTo ? formatDate(draftFilters.dateTo) : "Pick a date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={draftFilters.dateTo ? parseLocalDate(draftFilters.dateTo) : undefined}
                        onSelect={(date) => {
                          setDraftFilters({
                            ...draftFilters,
                            dateTo: date ? toLocalDateString(date) : undefined,
                          });
                          setDateToOpen(false);
                        }}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
            </div>

            <Separator />

            {/* Gross Amount Filter */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">Gross Amount</Label>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="modal-amount-min" className="text-sm">Min Amount</Label>
                  <Input
                    id="modal-amount-min"
                    type="number"
                    placeholder="0"
                    value={draftFilters.amountMin ?? ""}
                    onChange={(e) =>
                      setDraftFilters({
                        ...draftFilters,
                        amountMin: e.target.value ? Number(e.target.value) : undefined,
                      })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="modal-amount-max" className="text-sm">Max Amount</Label>
                  <Input
                    id="modal-amount-max"
                    type="number"
                    placeholder="No limit"
                    value={draftFilters.amountMax ?? ""}
                    onChange={(e) =>
                      setDraftFilters({
                        ...draftFilters,
                        amountMax: e.target.value ? Number(e.target.value) : undefined,
                      })
                    }
                  />
                </div>
              </div>
            </div>

            <Separator />

            {/* Flags Filters */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">Flags</Label>
              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="modal-filter-on-dispute"
                    checked={draftFilters.onDispute ?? false}
                    onCheckedChange={(checked) =>
                      setDraftFilters({
                        ...draftFilters,
                        onDispute: checked === true ? true : undefined,
                      })
                    }
                  />
                  <label
                    htmlFor="modal-filter-on-dispute"
                    className="text-sm font-medium leading-none cursor-pointer"
                  >
                    External Dispute
                  </label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="modal-filter-has-case"
                    checked={draftFilters.hasCase ?? false}
                    onCheckedChange={(checked) =>
                      setDraftFilters({
                        ...draftFilters,
                        hasCase: checked === true ? true : undefined,
                      })
                    }
                  />
                  <label
                    htmlFor="modal-filter-has-case"
                    className="text-sm font-medium leading-none cursor-pointer"
                  >
                    Has Collection Case
                  </label>
                </div>
              </div>
            </div>

            <Separator />

            {/* Outstanding Amount Filter */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">Outstanding Amount</Label>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="modal-outstanding-min" className="text-sm">Min Outstanding</Label>
                  <Input
                    id="modal-outstanding-min"
                    type="number"
                    placeholder="0"
                    value={draftFilters.outstandingBalanceMin ?? ""}
                    onChange={(e) =>
                      setDraftFilters({
                        ...draftFilters,
                        outstandingBalanceMin: e.target.value ? Number(e.target.value) : undefined,
                      })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="modal-outstanding-max" className="text-sm">Max Outstanding</Label>
                  <Input
                    id="modal-outstanding-max"
                    type="number"
                    placeholder="No limit"
                    value={draftFilters.outstandingBalanceMax ?? ""}
                    onChange={(e) =>
                      setDraftFilters({
                        ...draftFilters,
                        outstandingBalanceMax: e.target.value ? Number(e.target.value) : undefined,
                      })
                    }
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <SheetFooter className="p-6 bg-background border-t">
          <div className="flex gap-2 w-full">
            <Button variant="outline" onClick={handleClear} className="flex-1">
              Clear All
            </Button>
            <Button onClick={handleApply} className="flex-1">
              Apply Filters
            </Button>
          </div>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
